import { selectAccountId } from '../../../../../../../store/auth/authSlice'
import { Col, Container, Modal as BootstrapModal, Row, Spinner } from 'react-bootstrap'
import { INVALID_REFRESH_TOKEN_INTEGRATION, INVALID_WEALTHBOX_INTEGRATION } from '../../../../../../../common/errorMessages'
import { useAppDispatch, useAppSelector } from '../../../../../../../store/hooks'
import { RoomSession } from '../../../../../../../models/Room'
import { TrackEvent, useTracking } from '../../../../common/tracking'
import { selectMeetingInfoHTML } from '../../../../../../../store/meetingSummary/roomSession'
import {
  useGetAdditionalObjectsQuery,
  useGetIntegrationUsersQuery,
  useLazyGetIntegrationUsersQuery,
  useSaveMeetingMutation,
} from '../../../../../../../store/integrations/integrations'
import { useGetAccountQuery } from '../../../../../../../store/accountUserSettings/accountUserSettings'
import React, { useCallback, useEffect, useState } from 'react'
import { AccountIntegrationConfigMappingType, getAccountCRMIntegrationConfig } from '../../../../../../../models/Account'
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form'
import { Modal } from '../../../../../../../components/Modal/Modal'
import { $getRoot, $insertNodes, EditorState, LexicalEditor } from 'lexical'
import { SelectDropdown, SelectOption } from '../../../../../../../components/SelectDropdown/SelectDropdown'
import { getContactOptions } from '../../../../../../../views/MeetingSummary/CRM/utils'
import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html'
import { WealthboxIntegrationRow } from '../../../../../../../views/ProfileSettings/Integrations/Wealthbox/WealthboxIntegrationRow'
import { SalesforceIntegrationRow } from '../../../../../../../views/ProfileSettings/Integrations/Salesforce/SalesforceIntegrationRow'
import { ContactFormSelectDropdown } from '../../../../../../../views/MeetingSummary/CRM/ContactDropdown'
import { WritableLexicalEditor } from '../../../MeetingResults/Lexical/WritableLexicalEditor/WritableLexicalEditor'
import { LEXICAL_NODES } from '../../../MeetingResults/Lexical/nodes'
import { RichTextToolbarPlugin } from '../../../MeetingResults/Lexical/Plugins/RichTextToolbarPlugin/RichTextToolbarPlugin'
import { useMeetingSummaryElementsCopy } from '../CopyButton/hooks'
import { HTML_TYPE } from '../../../copy'
import { useIntegrationExportConfirmation } from '../../../../../../../components/ConfirmationDialog/useIntegrationExportConfirmation'
import { UserSelectDropdown } from '../../../../../../../views/MeetingSummary/CRM/UserDropdown'
import { TeamSelectDropdown } from '../../../../../../../views/MeetingSummary/CRM/TeamDropdown'
import { selectMeetingSummaryAgentAiAgentResultId, setMeetingSummary } from '../../../../../../../store/meetingSummaryAgent/meetingSummaryAgent'
import { useLazyGetAiAgentResultQuery } from '../../../../../../../store/ai/aiAgentResult'
import { AIAgentType } from '../../../../../../../models/AIAgent'
import { selectSelectedMeetingTemplate } from '../../../../../../../store/meetingSummary/meetingTemplates'
import { defaultCrmMeetingExportObject } from '../../../../../../../views/Integration/IntegrationManagement/IntegrationSpecificSettings/CRMIntegrationSettings'
import { IntegrationSavedModalBody } from '../../../../../../../components/Integrations/IntegrationSavedModalBody/IntegrationSavedModalBody'
import { useSyncStatuses } from '../../../ExternalUpdates/hooks'
import { addToast } from '../../../../../../../store/toast/toastSlice'

const INVALID_INTEGRATION_ERROR_MESSAGES = [INVALID_REFRESH_TOKEN_INTEGRATION, INVALID_WEALTHBOX_INTEGRATION]
const DEFAULT_ACCOUNT_CONFIG_MAPPING_SESSION = 'note'

interface CRMSessionObjectMapping {
  [key: string]: string
}

const CRM_SESSION_OBJECT: CRMSessionObjectMapping = {
  wealthbox: 'events',
  redtail: 'activities',
  salesforce: 'Event',
  practifi: 'Event',
  xlr8: 'Event',
  zoho: 'Events',
  elements: 'Event',
  blackdiamond: 'Event',
}

type MappingConfig = {
  path: string
  idKey: string
  labelKey: string
  household: HouseholdEventConfig | undefined
}

type HouseholdEventConfig = {
  householdPath: string
  eventPath: string
}

const eventConfigMap: Record<string, MappingConfig> = {
  wealthbox: { path: 'events', idKey: 'id', labelKey: 'title', household: undefined },
  redtail: { path: 'activities', idKey: 'id', labelKey: 'subject', household: undefined },
  salesforce: {
    path: 'events',
    idKey: 'Id',
    labelKey: 'Subject',
    household: { householdPath: 'household', eventPath: 'events' },
  },
  practifi: {
    path: 'events',
    idKey: 'Id',
    labelKey: 'Subject',
    household: { householdPath: 'household', eventPath: 'events' },
  },
  xlr8: {
    path: 'events',
    idKey: 'Id',
    labelKey: 'Subject',
    household: { householdPath: 'household', eventPath: 'events' },
  },
  zoho: {
    path: 'events',
    idKey: 'id',
    labelKey: 'Event_Title',
    household: { householdPath: 'household', eventPath: 'events' },
  },
  elements: {
    path: 'events',
    idKey: 'Id',
    labelKey: 'Subject',
    household: { householdPath: 'household', eventPath: 'events' },
  },
  blackdiamond: {
    path: 'events',
    idKey: 'Id',
    labelKey: 'Subject',
    household: { householdPath: 'household', eventPath: 'events' },
  },
}

const extractEvents = (data: any, config: MappingConfig): { id: string; label: string }[] => {
  const contactCollection = data?.[config.path]
  let contactEvents: { id: string; label: string }[] = []
  if (Array.isArray(contactCollection)) {
    contactEvents = contactCollection.map((item) => ({
      id: `${item[config.idKey]}`,
      label: `${item[config.labelKey]}`,
    }))
  }

  let householdEvents: { id: string; label: string }[] = []
  if (config.household) {
    const householdCollection = data?.[config.household.householdPath]?.[config.household.eventPath]
    if (Array.isArray(householdCollection)) {
      householdEvents = householdCollection.map((item) => ({
        id: `${item[config.idKey]}`,
        label: `${item[config.labelKey]}`,
      }))
    }
  }

  return [...contactEvents, ...householdEvents].filter((event, index, self) => self.findIndex((e) => e.id === event.id) === index)
}

export interface AddAgentResultToCRMModalProps {
  roomSession: RoomSession
  show: boolean
  onHide: () => void
  serviceId: string
  onSave?: () => void
}

type FormValues = {
  externalUserId: string
  notifyUserId: string
  notifyTeamId: string
  initialMeetingDescription: string
  meetingDescription: string
  externalMeetingId: string | null
}

const EVENT_DATE_OFFSET = 7 * 24 * 60 * 60 * 1000

export const AddAgentResultToCRMModal: React.FC<AddAgentResultToCRMModalProps> = ({ show, onHide, roomSession, serviceId, onSave }) => {
  const accountId = useAppSelector(selectAccountId)
  const dispatch = useAppDispatch()
  const { tracked, trackingClient } = useTracking()
  const [saveMeeting, { isLoading }] = useSaveMeetingMutation()
  const meetingInfoHTML = useAppSelector(selectMeetingInfoHTML)
  const aiAgentResultId = useAppSelector(selectMeetingSummaryAgentAiAgentResultId)
  const { data: account } = useGetAccountQuery({ accountId })
  const [triggerGetAiAgentResult, { isLoading: isGetAiAgentResultLoading }] = useLazyGetAiAgentResultQuery()
  const selectedMeetingTemplate = useAppSelector(selectSelectedMeetingTemplate)
  const { runMutationWithConfirmation, ConfirmationModal, accountIsLoading } = useIntegrationExportConfirmation({ roomSession })
  const [isEventAppendSupported, setIsEventAppendSupported] = useState<boolean>(false)
  const [isNotifySupported, setIsNotifySupported] = useState<boolean>(false)
  const [savedMeetingResult, setSavedMeetingResult] = useState<boolean>(false)
  const { meetingSummarySyncStatus } = useSyncStatuses()
  const [sessionLabel, setSessionLabel] = useState<string>(DEFAULT_ACCOUNT_CONFIG_MAPPING_SESSION)
  const [contactObject, setContactObject] = useState<string>('Contact')

  const { getCopyContent } = useMeetingSummaryElementsCopy()

  useEffect(() => {
    if (!account || !account.data) {
      return
    }
    const serviceIntegration = getAccountCRMIntegrationConfig(account.data, serviceId)
    if (!serviceIntegration) {
      return
    }

    const sessionMapping =
      typeof serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION] === 'string'
        ? serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION]
        : serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION].objectName
    setSessionLabel(sessionMapping.charAt(0).toUpperCase() + sessionMapping.slice(1))

    const updatable =
      typeof serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION] === 'string'
        ? null
        : Object.keys(serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION].additionalInformation || {}).includes('updatable')
          ? serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION].additionalInformation?.updatable
          : null

    if (updatable === true || (updatable === null && sessionMapping === CRM_SESSION_OBJECT[serviceId])) {
      setIsEventAppendSupported(true)
    }

    const contactObject =
      typeof serviceIntegration.mappings[AccountIntegrationConfigMappingType.CONTACT] === 'string'
        ? serviceIntegration.mappings[AccountIntegrationConfigMappingType.CONTACT]
        : serviceIntegration.mappings[AccountIntegrationConfigMappingType.CONTACT].objectName
    if (serviceId === 'salesforce') {
      setContactObject(contactObject)
    }
    if (sessionMapping === defaultCrmMeetingExportObject['redtail'].id && serviceId === 'redtail') {
      setIsNotifySupported(true)
    }
  }, [account, serviceId])

  const initialMeetingDescription = getCopyContent()[HTML_TYPE]

  const defaultValues = {
    externalUserId: '',
    initialMeetingDescription,
    meetingDescription: initialMeetingDescription,
    externalMeetingId: '',
  }

  const _getDescription = (originalDescription: string) => {
    const serviceIntegration = getAccountCRMIntegrationConfig(account?.data, serviceId)
    const accountConfigMappingSession = serviceIntegration
      ? typeof serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION] === 'string'
        ? serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION]
        : serviceIntegration.mappings[AccountIntegrationConfigMappingType.SESSION].objectName
      : DEFAULT_ACCOUNT_CONFIG_MAPPING_SESSION

    if (accountConfigMappingSession === CRM_SESSION_OBJECT[serviceId]) {
      return originalDescription
    }

    return `<div>${meetingInfoHTML} ${originalDescription}</div>`
  }

  const handlePrimaryAction = async () => {
    await handleSubmit(handleSave)()
    if (onSave) {
      onSave()
    }
  }

  const handleSave = tracked(
    async (data: FormValues) => {
      const description = _getDescription(data.meetingDescription)
      try {
        if (data.notifyUserId) {
          trackingClient.track({
            eventName: TrackEvent.NOTIFY_USER_UTILIZED,
            eventDetails: {
              type: 'user',
              serviceId: serviceId,
            },
          })
        }
        if (data.notifyTeamId) {
          trackingClient.track({
            eventName: TrackEvent.NOTIFY_USER_UTILIZED,
            eventDetails: {
              type: 'team',
              serviceId: serviceId,
            },
          })
        }

        await runMutationWithConfirmation(async () => {
          const response = await saveMeeting({
            accountId,
            connector: serviceId,
            body: {
              roomId: roomSession.roomId,
              sessionId: roomSession.id,
              description: description,
              ...(data.notifyUserId ? { notifyUserId: data.notifyUserId } : {}),
              ...(data.notifyTeamId ? { notifyTeamId: data.notifyTeamId } : {}),
              externalUserIds: [data.externalUserId],
              externalMeetingId: data.externalMeetingId,
              relatedTo: { id: data.externalUserId, type: contactObject },
              exportType: 'meetingSummary',
              aiAgentResultId: aiAgentResultId,
            },
          })
          if (!response.error) {
            const existingMeetingSummary = await triggerGetAiAgentResult(
              {
                accountId,
                type: AIAgentType.MEETING_SUMMARY,
                resultKey: `${roomSession.roomId}|${roomSession.id}|${selectedMeetingTemplate?.id}`,
              },
              false,
            ).unwrap()
            if (existingMeetingSummary) {
              dispatch(setMeetingSummary(existingMeetingSummary))
              setSavedMeetingResult(true)
            }
          }
        })
      } catch (e) {
        console.error(e)
      }
    },
    { eventName: TrackEvent.SAVE_MEETING_SUMMARY_TO_CRM },
    {
      eventDetailsExtractor: (data: FormValues) => {
        return {
          roomId: roomSession.roomId,
          sessionId: roomSession.id,
          externalUserId: data.externalUserId,
          connector: serviceId,
        }
      },
    },
  )

  const form = useForm<FormValues>({ defaultValues })
  const { handleSubmit, setValue } = form

  const handleOnHide = useCallback(() => {
    form.reset()
    onHide()
  }, [onHide, form])

  useEffect(() => {
    if (savedMeetingResult && !meetingSummarySyncStatus.length) {
      dispatch(
        addToast({
          variant: 'success',
          title: 'Meeting Summary saved',
          messages: ['Meeting Summary was successfully saved to your CRM'],
        }),
      )
      handleOnHide()
    }
  }, [savedMeetingResult, meetingSummarySyncStatus, handleOnHide, dispatch])

  useEffect(() => {
    setValue('initialMeetingDescription', initialMeetingDescription)
    setValue('meetingDescription', initialMeetingDescription)
  }, [setValue, initialMeetingDescription])

  return (
    <>
      <Modal
        size='xl'
        show={show}
        onHide={handleOnHide}
        header={
          !savedMeetingResult ? (
            <BootstrapModal.Title className='w-100 d-flex align-items-center justify-content-center'>
              Save your meeting to&nbsp;<span className='text-capitalize'>{serviceId}</span>
            </BootstrapModal.Title>
          ) : null
        }
        body={
          <FormProvider {...form}>
            {savedMeetingResult && meetingSummarySyncStatus.length ? (
              <IntegrationSavedModalBody syncStatuses={[meetingSummarySyncStatus[0]]} serviceId={serviceId} onHide={handleOnHide} />
            ) : (
              <AddMeetingToCRMModalBody
                roomSession={roomSession}
                serviceId={serviceId}
                isEventAppendSupported={isEventAppendSupported}
                isNotifySupported={isNotifySupported}
                sessionLabel={sessionLabel}
              />
            )}
          </FormProvider>
        }
        showFooterActions={!savedMeetingResult}
        onFooterPrimaryAction={handlePrimaryAction}
        footerPrimaryActionLoading={isLoading || accountIsLoading || isGetAiAgentResultLoading}
        onFooterSecondaryAction={onHide}
        centered={true}
      />
      <ConfirmationModal />
    </>
  )
}

const AddMeetingToCRMModalBody: React.FC<{
  roomSession: RoomSession
  serviceId: string
  isEventAppendSupported: boolean
  isNotifySupported: boolean
  sessionLabel: string
}> = ({ roomSession, serviceId, isEventAppendSupported, isNotifySupported, sessionLabel }) => {
  const accountId = useAppSelector(selectAccountId)
  const { control, setValue, watch } = useFormContext<FormValues>()
  const selectedExternalUserId = watch('externalUserId')
  const initialMeetingDescription = watch('initialMeetingDescription')
  const { trackingClient } = useTracking()
  const [isModified, setIsModified] = useState<boolean>(false)
  const [isInitialized, setIsInitialized] = useState<boolean>(false)

  const {
    data: contacts,
    isFetching: isContactsFetching,
    isError: isContactsError,
    error: contactsError,
  } = useGetIntegrationUsersQuery({ accountId, connector: serviceId })

  const [triggerGetContacts, { isError: isTriggerGetContactsError, error: triggerContactsError }] = useLazyGetIntegrationUsersQuery()
  const [editor, setEditor] = useState<LexicalEditor | null>(null)
  const [contactOptions, setContactOptions] = useState<SelectOption[]>([])
  const [eventOptions, setEventOptions] = useState<SelectOption[]>([])

  const { data: additionalObjects, isFetching: isAdditionalObjectsFetching } = useGetAdditionalObjectsQuery(
    {
      accountId,
      connector: serviceId,
      relatedTo: { id: selectedExternalUserId },
      meetingStartDate: new Date(new Date(roomSession.startedAt).getTime() - EVENT_DATE_OFFSET).toISOString(),
      meetingEndDate: new Date(new Date(roomSession.finishedAt ?? roomSession.startedAt).getTime() + EVENT_DATE_OFFSET).toISOString(),
    },
    { skip: !selectedExternalUserId || !isEventAppendSupported },
  )
  useEffect(() => {
    if (contacts && contacts.data) {
      setContactOptions(getContactOptions(contacts.data, serviceId))
    }
  }, [contacts, serviceId])

  useEffect(() => {
    if (!editor) {
      return
    }

    editor.update(() => {
      const parser = new DOMParser()
      const dom = parser.parseFromString(initialMeetingDescription, 'text/html')
      const nodes = $generateNodesFromDOM(editor, dom)
      $getRoot().select()
      $insertNodes(nodes)
    })
  }, [editor, initialMeetingDescription])

  useEffect(() => {
    if (!additionalObjects) {
      return
    }
    const config = eventConfigMap[serviceId]
    if (config) {
      setEventOptions(extractEvents(additionalObjects, config))
    }
  }, [additionalObjects, serviceId])

  const onSearchInputChange = async (searchInput: string) => {
    try {
      const contactsData = await triggerGetContacts({
        accountId,
        connector: serviceId,
        filters: { name: searchInput },
      }).unwrap()
      if (contactsData && contactsData.data) {
        setContactOptions(getContactOptions(contactsData.data, serviceId))
      }
    } catch (e) {
      console.error(e)
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleTextContentChange = (textContent: string) => {
    if (!isModified && isInitialized) {
      setIsModified(true)
      trackingClient.track({
        eventName: TrackEvent.MEETING_SUMMARY_EDITED_IN_MODAL,
        eventDetails: {
          serviceId: serviceId,
        },
      })
    }
    if (!isInitialized) {
      setIsInitialized(true)
    }
  }

  const handleOnChange = (editorState: EditorState, editor: LexicalEditor) => {
    const htmlValue = editorState.read(() => {
      return $generateHtmlFromNodes(editor, null)
    })

    setValue('meetingDescription', htmlValue)
  }

  if (isContactsFetching && !contacts) {
    return (
      <div className='d-flex justify-content-center'>
        <Spinner />
      </div>
    )
  }

  const getConnectorIntegrationRow = () => {
    if (serviceId === 'wealthbox') {
      return <WealthboxIntegrationRow useIntegrationRedirectUri />
    }
    if (serviceId === 'salesforce') {
      return <SalesforceIntegrationRow useIntegrationRedirectUri />
    }
  }

  if (
    (isContactsError && INVALID_INTEGRATION_ERROR_MESSAGES.some((ignoredMessage) => JSON.stringify(contactsError).match(ignoredMessage))) ||
    (isTriggerGetContactsError && INVALID_INTEGRATION_ERROR_MESSAGES.some((ignoredMessage) => JSON.stringify(triggerContactsError).match(ignoredMessage)))
  ) {
    return (
      <>
        <Container className='p-0'>
          <Row>
            <Col>Your integration is invalid please reconnect your integration:</Col>
          </Row>
        </Container>
        {getConnectorIntegrationRow()}
      </>
    )
  }

  return (
    <Container>
      <Row className='mt-3'>
        <Col xs={12}>
          <ContactFormSelectDropdown
            fieldName='externalUserId'
            contactOptions={contactOptions}
            serviceId={serviceId}
            onSearchInputChange={onSearchInputChange}
            onOptionSelected={() => setValue('externalMeetingId', null)}
          />
        </Col>
      </Row>
      {isNotifySupported && (
        <div>
          <Row className='mt-3'>
            <Col xs={12}>
              <div className='lead-extra-small'>Notify user</div>
              <Controller
                name='notifyUserId'
                control={control}
                render={({ field }) => <UserSelectDropdown serviceId={serviceId} onOptionSelected={field.onChange} />}
              />
            </Col>
          </Row>
          <Row className='mt-3'>
            <Col xs={12}>
              <div className='lead-extra-small'>Notify team</div>
              <Controller
                name='notifyTeamId'
                control={control}
                render={({ field }) => <TeamSelectDropdown serviceId={serviceId} onOptionSelected={field.onChange} />}
              />
            </Col>
          </Row>
        </div>
      )}
      {isEventAppendSupported ? (
        <Row className='mt-3'>
          <Col xs={12}>
            <div className='lead-extra-small'>{sessionLabel} (Optional)</div>
            <Controller
              name='externalMeetingId'
              control={control}
              render={({ field }) => {
                const option = field.value ? eventOptions.find((e) => e.id === field.value) : null
                return (
                  <SelectDropdown
                    initialSelectedOption={option ?? null}
                    options={eventOptions}
                    className='py-2 zocks-px-0.75'
                    onOptionSelected={(option) => field.onChange(option.id)}
                    onDeselect={() => field.onChange(null)}
                    placeholder={`Select your ${sessionLabel}`}
                    deselectOptionLabel={`Select your ${sessionLabel}`}
                    loading={isAdditionalObjectsFetching}
                    showDeselectOption={true}
                  />
                )
              }}
            />
          </Col>
        </Row>
      ) : null}
      <Row className='mt-3'>
        <Col>
          <div className='lead-extra-small'>Description</div>
        </Col>
      </Row>
      <Row className='mt-1 gy-3 zocks-bg-color-gray-100 zocks-p-0.75'>
        <Col>
          <Controller
            name='meetingDescription'
            control={control}
            render={() => (
              <WritableLexicalEditor
                ref={(editor) => setEditor(editor)}
                onChange={handleOnChange}
                onTextContentChange={handleTextContentChange}
                className='flex-column-reverse align-items-start'
                editorContainerClassName='flex-grow-1 add-meeting-to-crm-editor-container'
                containerClassName='w-100'
                lexicalNodes={LEXICAL_NODES}
                header={
                  <RichTextToolbarPlugin className='px-3 zocks-py-0.75 gap-2'>
                    <div className='editable-text-result-rich-text-divider' />
                  </RichTextToolbarPlugin>
                }
              />
            )}
          />
        </Col>
      </Row>
    </Container>
  )
}
